import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { CrewMember } from "@/mocks/admin/crew";
import { buildMoverUrl } from "@/lib/moverLink";
import CrewStatusBadge from "./CrewStatusBadge";

const DAYS: ("M" | "T" | "W" | "Th" | "F" | "Sa" | "Su")[] = ["M", "T", "W", "Th", "F", "Sa", "Su"];

export default function CrewCard({ member }: { member: CrewMember }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const utilization = Math.min(100, Math.round((member.weeklyHours / member.weeklyTarget) * 100));
  const utilizationColor =
    utilization >= 90 ? "bg-emerald-500" : utilization >= 50 ? "bg-brand-500" : "bg-amber-500";

  const moverUrl = buildMoverUrl(member.accessToken);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const copy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!moverUrl) return;
    try {
      await navigator.clipboard.writeText(moverUrl);
    } catch {
      // ignore
    }
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setMenuOpen(false);
    }, 1400);
  };

  return (
    <Link
      to={`/crew/${member.id}`}
      className="group relative bg-white border border-ink-100 rounded-lg p-5 hover:border-ink-300 transition-colors flex flex-col cursor-pointer"
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className={`w-12 h-12 rounded-full ${member.color} text-white text-sm font-bold flex items-center justify-center shrink-0`}
        >
          {member.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-ink-900 truncate group-hover:text-brand-700">
              {member.name}
            </h3>
          </div>
          <div className="text-[11px] text-ink-500 mt-0.5">
            {member.role} · <span className="font-mono">{member.id}</span>
          </div>
          <div className="mt-1.5">
            <CrewStatusBadge status={member.status} />
          </div>
        </div>
        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-md cursor-pointer ${
              menuOpen ? "bg-cream-100 text-ink-700" : "hover:bg-cream-100 text-ink-400"
            }`}
            aria-label="More actions"
          >
            <i className="ri-more-2-fill text-sm"></i>
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-9 z-20 w-56 bg-white border border-ink-100 rounded-md py-1 text-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <button
                onClick={copy}
                disabled={!moverUrl}
                className="w-full text-left px-3 py-2 hover:bg-cream-50 cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i
                  className={
                    copied ? "ri-check-line text-emerald-600" : "ri-link text-ink-500"
                  }
                ></i>
                <span className="font-semibold text-ink-900">
                  {copied ? "Link copied!" : "Copy mover app link"}
                </span>
              </button>
              {moverUrl && (
                <a
                  href={moverUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="block px-3 py-2 hover:bg-cream-50 cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-external-link-line text-ink-500"></i>
                  <span className="text-ink-700">Preview their app</span>
                </a>
              )}
              <div className="border-t border-ink-100 my-1"></div>
              <a
                href={`tel:${member.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="block px-3 py-2 hover:bg-cream-50 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-phone-line text-ink-500"></i>
                <span className="text-ink-700">Call</span>
              </a>
              <a
                href={`mailto:${member.email}`}
                onClick={(e) => e.stopPropagation()}
                className="block px-3 py-2 hover:bg-cream-50 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-mail-line text-ink-500"></i>
                <span className="text-ink-700">Email</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Stats trio */}
      <div className="grid grid-cols-3 gap-2 py-3 border-y border-ink-100 mb-3 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Moves</div>
          <div className="font-display text-base font-bold text-ink-900">{member.completedMoves}</div>
        </div>
        <div className="border-x border-ink-100">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Rating</div>
          <div className="font-display text-base font-bold text-ink-900 inline-flex items-center gap-0.5">
            <i className="ri-star-fill text-amber-500 text-xs"></i>
            {member.rating}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">Rate</div>
          <div className="font-display text-base font-bold text-ink-900">${member.hourlyRate}/h</div>
        </div>
      </div>

      {/* Weekly utilization */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] mb-1.5">
          <span className="text-ink-500 font-semibold">This Week</span>
          <span className="text-ink-900 font-bold tabular-nums">
            {member.weeklyHours}h / {member.weeklyTarget}h
          </span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-cream-100 overflow-hidden">
          <div
            className={`h-full ${utilizationColor} rounded-full transition-all`}
            style={{ width: `${utilization}%` }}
          ></div>
        </div>
      </div>

      {/* Availability */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-1.5">
          Available days
        </div>
        <div className="flex items-center gap-1">
          {DAYS.map((d) => {
            const av = member.availability.includes(d);
            return (
              <span
                key={d}
                className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center ${
                  av ? "bg-brand-100 text-brand-700" : "bg-cream-100 text-ink-300"
                }`}
              >
                {d}
              </span>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-ink-100 flex items-center justify-between text-[11px] text-ink-500">
        {member.currentProject ? (
          <span className="inline-flex items-center gap-1 text-brand-700 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse"></span>
            On {member.currentProject}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <i className="ri-map-pin-line text-ink-400"></i>
            {member.city}
          </span>
        )}
        <span className="text-ink-400">Hired {member.hireDate}</span>
      </div>
    </Link>
  );
}